import React from 'react';
import PropTypes from 'prop-types';
import {
    Grid,
    Box,
    Tooltip,
    Typography,
    Stack,
    IconButton,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { Drawer } from './styles';
import { OXEMSLogoIcon, OXESymbolIcon } from '../../Icons';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import SideMenuContent from './SideMenuContent';
import SettingsListItem from './SettingsListItem';
import { MailOutline } from '@mui/icons-material';

const Dashboard = ({
    children,
    sideMenuContent,
    sideMenuOpen,
    setSideMenuOpen,
    themeMode,
    handleThemeChange,
    user,
    manualContainerStyles,
    handleFaqClick,
    hideMenu,
    handleLogoutClick,
    feedbackEmail,
    isAuthenticated,
    handleLoginClick
}) => {
    const { profile } = user || {};
    const { firstName } = profile || {};

    return (
        <Box sx={{ display: 'flex' }}>
            <Drawer
                sx={{ background: 'transparent', display: hideMenu ? 'none' : 'flex' }}
                PaperProps={{
                    sx: {
                        overflow: 'hidden',
                        background: (theme) => theme.palette.navbar.background,
                        color: (theme) => theme.palette.navbar.color
                    }
                }}
                variant="permanent"
                open={sideMenuOpen}>
                <Stack sx={{ height: '100vh' }}>
                    <Stack
                        spacing={2}
                        sx={{
                            height: '80%',
                            background: (theme) => theme.palette.navbar.background
                        }}>
                        <Stack
                            sx={{ pl: 2, pt: 3, pr: 2 }}
                            direction={'row'}
                            justifyContent={'space-between'}>
                            <Stack direction={'row'} alignItems={'center'}>
                                {sideMenuOpen ? (
                                    <OXEMSLogoIcon height={40} width={190} />
                                ) : (
                                    <OXESymbolIcon height={24} width={24} />
                                )}
                            </Stack>
                            {sideMenuOpen && (
                                <Stack spacing={1} direction={'row'}>
                                    <IconButton
                                        sx={{
                                            color: (theme) => theme.palette.navbar.color,
                                            borderRadius: '50%', // Make the button circular
                                            '&:hover': {
                                                backgroundColor: (theme) =>
                                                    theme.palette.navbar.backgroundHover
                                            },
                                            width: '40px',
                                            height: '40px'
                                        }}
                                        onClick={() => setSideMenuOpen(false)}>
                                        <KeyboardDoubleArrowLeftIcon
                                            sx={{ fontSize: '20px' }}
                                            color={'inherit'}
                                        />
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                        <Stack direction={'row'} sx={{ paddingTop: '0 !important' }}>
                            {sideMenuOpen ? (
                                <Stack sx={{ minWidth: '100%', pl: 2, pr: 2 }}>
                                    <Typography variant={'overline'}>welcome</Typography>
                                    <Typography
                                        sx={{ position: 'relative', bottom: '10px' }}
                                        variant={'h6'}>
                                        {firstName}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack
                                    alignSelf={'center'}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: (theme) =>
                                                theme.palette.navbar.backgroundHover
                                        }
                                    }}>
                                    <Tooltip title={'Expand menu'} placement="right" arrow>
                                        <div>
                                            <ListItemButton
                                                sx={{ p: 2 }}
                                                onClick={() => setSideMenuOpen(true)}>
                                                <ListItemIcon
                                                    sx={{ color: '#fff', minWidth: '48px' }}>
                                                    <KeyboardDoubleArrowRightIcon
                                                        sx={{ fontSize: '24px' }}
                                                        color={'inherit'}
                                                    />
                                                </ListItemIcon>
                                            </ListItemButton>
                                        </div>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Stack>
                        <Stack
                            sx={{
                                paddingTop: '0px !important',
                                maxHeight: '80%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                '::-webkit-scrollbar': {
                                    width: '7px'
                                },

                                /* Track */
                                '::-webkit-scrollbar-track': {
                                    borderRadius: '10px',
                                    background: (theme) => theme.palette.navbar.backgroundHover
                                },

                                /* Handle */
                                ' ::-webkit-scrollbar-thumb': {
                                    background: (theme) => theme.palette.primary.main,
                                    borderRadius: '10px'
                                }
                            }}>
                            <SideMenuContent
                                menuOpen={sideMenuOpen}
                                sideMenuItems={sideMenuContent}
                            />
                        </Stack>
                    </Stack>

                    <Grid container sx={{ height: '20%' }}>
                        <Grid item xs={12}>
                            <Divider sx={{ borderColor: '#373535' }} />
                        </Grid>
                        <Grid item xs={12}>
                            <List>
                                {handleThemeChange && (
                                    <SettingsListItem
                                        menuOpen={sideMenuOpen}
                                        handleThemeChange={handleThemeChange}
                                        themeMode={themeMode}
                                    />
                                )}
                                {feedbackEmail && (
                                    <ListItemButton
                                        href={`mailto:${feedbackEmail}`}
                                        sx={{
                                            '&:hover': {
                                                background: (theme) =>
                                                    theme.palette.navbar.backgroundHover
                                            }
                                        }}
                                        onClick={handleFaqClick}>
                                        <ListItemIcon sx={{ color: '#fff', minWidth: '48px' }}>
                                            <MailOutline />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { fontSize: '0.95rem' }
                                            }}
                                            style={{ fontWeight: 'bold' }}
                                            primary={'Feedback'}
                                        />
                                    </ListItemButton>
                                )}
                                {handleFaqClick && (
                                    <ListItemButton
                                        sx={{
                                            '&:hover': {
                                                background: (theme) =>
                                                    theme.palette.navbar.backgroundHover
                                            }
                                        }}
                                        onClick={handleFaqClick}>
                                        <ListItemIcon sx={{ color: '#fff', minWidth: '48px' }}>
                                            <HelpOutlineOutlinedIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { fontSize: '0.95rem' }
                                            }}
                                            style={{ fontWeight: 'bold' }}
                                            primary={'FAQ'}
                                        />
                                    </ListItemButton>
                                )}
                                {isAuthenticated ? (
                                    <ListItemButton
                                        sx={{
                                            '&:hover': {
                                                background: (theme) =>
                                                    theme.palette.navbar.backgroundHover
                                            }
                                        }}
                                        onClick={handleLogoutClick}>
                                        <ListItemIcon sx={{ color: '#fff', minWidth: '48px' }}>
                                            <LogoutIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { fontSize: '0.95rem' }
                                            }}
                                            style={{ fontWeight: 'bold' }}
                                            primary={'Log out'}
                                        />
                                    </ListItemButton>
                                ) : (
                                    <ListItemButton
                                        sx={{
                                            '&:hover': {
                                                background: (theme) =>
                                                    theme.palette.navbar.backgroundHover
                                            }
                                        }}
                                        onClick={handleLoginClick}>
                                        <ListItemIcon sx={{ color: '#fff', minWidth: '48px' }}>
                                            <LoginIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { fontSize: '0.95rem' }
                                            }}
                                            style={{ fontWeight: 'bold' }}
                                            primary={'Login'}
                                        />
                                    </ListItemButton>
                                )}
                            </List>
                        </Grid>
                    </Grid>
                </Stack>
            </Drawer>
            <Box
                component="main"
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    maxWidth: sideMenuOpen ? 'calc(100vw - 284px)' : 'calc(100vw - 76px)'
                }}>
                <Grid
                    container
                    sx={{
                        padding: manualContainerStyles ? '' : '1em',
                        mb: manualContainerStyles ? '' : '80px',
                        overflow: 'hidden'
                    }}>
                    {children}
                </Grid>
            </Box>
        </Box>
    );
};

Dashboard.propTypes = {
    children: PropTypes.node,
    sideMenuContent: PropTypes.arrayOf(PropTypes.object),
    sideMenuOpen: PropTypes.bool.isRequired,
    setSideMenuOpen: PropTypes.func.isRequired,
    handleLogoutClick: PropTypes.func.isRequired,
    themeMode: PropTypes.string,
    feedbackEmail: PropTypes.string,
    handleThemeChange: PropTypes.func,
    handleFaqClick: PropTypes.func,
    user: PropTypes.object,
    hideMenu: PropTypes.bool,
    manualContainerStyles: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    handleLoginClick: PropTypes.func
};

Dashboard.defaultProps = {
    children: null,
    sideMenuContent: [],
    themeMode: null,
    feedbackEmail: null,
    handleThemeChange: null,
    user: null,
    handleFaqClick: null,
    hideMenu: false,
    manualContainerStyles: false,
    isAuthenticated: true
};

export default Dashboard;
